import {Injectable} from '@angular/core';

@Injectable({
				providedIn: 'root'
			})
export class WebAuthnService {
	private dbName = 'logr-webauthn-db';
	private storeName = 'keys';

	async clearKeys(): Promise<void> {
		const db = await this.getDb();
		const tx = db.transaction(this.storeName, 'readwrite');
		tx.objectStore(this.storeName).clear();
		return new Promise((resolve, reject) => {
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
		});
	}


	async registerCredential(rawKey: ArrayBuffer): Promise<void> {
		const publicKey: PublicKeyCredentialCreationOptions = {
			challenge             : crypto.getRandomValues(new Uint8Array(32)),
			rp                    : {
				id  : 'logr.sharkey.cloud',
				name: 'Logr App'
			},
			user                  : {
				id         : new TextEncoder().encode('logr-user'),
				name       : 'logr@sharkey.cloud',
				displayName: 'Logr User'
			},
			pubKeyCredParams      : [
				{
					alg : -7,
					type: 'public-key'
				}
			],
			authenticatorSelection: {
				authenticatorAttachment: 'platform',
				residentKey            : 'required',
				userVerification       : 'required'
			},
			timeout               : 60000,
			attestation           : 'none'
		};

		try {
			const credential = await navigator.credentials.create({publicKey}) as PublicKeyCredential;
			if (!credential) throw new Error('No credential returned');

			console.log('📌 Registered credential ID:', credential.id);

			// Save AES key under credential ID
			await this.saveWrappedKey(credential.id, rawKey);
		} catch (error) {
			console.error('❌ Error registering credential:', error);
			throw error;
		}
	}

	/*
	 async getWrappedKeyWithFaceId(keyId: string): Promise<ArrayBuffer | null> {
	 const publicKey: PublicKeyCredentialRequestOptions = {
	 challenge       : crypto.getRandomValues(new Uint8Array(32)),
	 rpId            : 'logr.sharkey.cloud',
	 userVerification: 'required',
	 timeout         : 60000
	 };

	 const assertion = await navigator.credentials.get({publicKey});
	 if (!assertion) return null;

	 // Pull stored AES key from IndexedDB using credential ID
	 return await this.loadWrappedKey(keyId) || null; // existing IndexedDB load method
	 }
	 */

	async getAssertion(): Promise<PublicKeyCredential | null> {
		const publicKey: PublicKeyCredentialRequestOptions = {
			challenge       : crypto.getRandomValues(new Uint8Array(32)),
			rpId            : 'logr.sharkey.cloud',
			userVerification: 'required',
			timeout         : 60000
		};

		try {
			const credential = await navigator.credentials.get({publicKey});
			return credential as PublicKeyCredential;
		} catch (error) {
			console.error('Error getting assertion:', error);
			return null;
		}
	}

	private async getDb(): Promise<IDBDatabase> {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, 1);
			request.onupgradeneeded = () => {
				request.result.createObjectStore(this.storeName);
			};
			request.onsuccess = () => resolve(request.result);
			request.onerror = () => reject(request.error);
		});
	}

	async saveWrappedKey(id: string, wrappedKey: ArrayBuffer): Promise<void> {
		const db = await this.getDb();
		const tx = db.transaction(this.storeName, 'readwrite');
		tx.objectStore(this.storeName).put(wrappedKey, id);
		return new Promise((resolve, reject) => {
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
		});
	}

	async loadWrappedKey(id: string): Promise<ArrayBuffer | null> {
		const db = await this.getDb();
		const tx = db.transaction(this.storeName, 'readonly');
		const request = tx.objectStore(this.storeName).get(id);
		return new Promise((resolve, reject) => {
			request.onsuccess = () => {
				if (!request.result) {
					console.warn('❌ No key found in IndexedDB for credential ID:', id);
					resolve(null);
				} else {
					resolve(request.result as ArrayBuffer);
				}
			};
			request.onerror = () => {
				console.error('❌ Error loading key from IndexedDB:', request.error);
				reject(request.error);
			};
		});
	}
}
